'use client'
/***
 *
 * @file layouts/Section/index.tsx
 * @description Section layout will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Center
 * @since 13 Feb 2024
 *
 */

import React, { ReactNode } from "react";
// import "./Section.scss";
import { StyledSection } from "@/shared/StyledComponents";
import classNames from "classnames";
import { T_RLData } from "@/types/contentful.type";

/** defines type of props passed to Section functional component */
type T_Props = {
  /** child component to be rendered inside a Section */
  children?: ReactNode,

  /** Unique identifier for a section */
  id: string | undefined,

  /** content type */
  typeid: string,

  /** content data to be used to render the contents */
  data: T_RLData,

  /** CSS class to be passed inside the section component */
  className?: string,

  /** CSS styles to be passed in the style attribute of the section component */
  customStyles?: React.CSSProperties
}

/**
 * Name: Section
 * 
 * Description: It takes in children as props and renders them within a section component
 * 
 * @param param0 object containing the children to be rendered and other props
 * @example Parameter breakup
 * ```
 *  param0: {
 *    children,          //children components to be rendered
 *    className: "",     //className passed via contentful
 *    customStyles: {},  //style object passed via contentful
 *    data: {},          //content data to be used to render the content
 *    typeid: "",        //content type
 *    id: ""             
 *  }
 * ```
 * @example T_Props
 * ```
 *  type T_Props = {
 *      children?: ReactNode,   // optional
 *      id: string,    
 *      typeid: string, 
 *      data: any,           
 *      className?: string,     // optional
 *      customStyles?: any      // optional
 * }
 * ```
 * @returns a client side component that renders its children inside a section component
 */
const Section = ({ children, ...props }: T_Props) => {

  return (
    <StyledSection id={props?.id || "sect"} className={classNames(`bp-section`, `container`, props?.className)} data-testid="Section" style={{ ...props?.customStyles }}>
      {/* <Row id={`sect-${props?.id}-row`}>
        <AddColumn clContent={content} parentId={`sect-${props?.id}-row`} {...props} />
      </Row> */}
      <div className="section-container">{children}</div>
    </StyledSection>
  );
};

export default Section;
